<template>
  <div>
    <div v-if="printJobs.length > 0">
      <div class="list-header mb-3">
        <h5 class="mb-0">
          <CIcon name="cilPrint" class="mr-2"/>
          <slot name="title">
            <translate translate-context="edge-prints.jobs">Print Jobs</translate>
          </slot>
        </h5>
      </div>
      <div class="print-jobs-container">
        <div
          v-for="job in printJobs"
          :key="job.id"
          class="print-job-item"
        >
          <!-- Job Header -->
          <div class="print-job-header">
            <div class="print-job-info">
              <div class="d-flex align-items-center">
                <div class="job-specs">
                  <CBadge v-if="job.print_config && job.print_config.format_cm" color="info" class="mr-2">
                    <CIcon name="cilFullscreen" size="sm" class="mr-1"/>
                    {{ job.print_config.format_cm }}
                  </CBadge>
                  <CBadge v-if="job.print_config && job.print_config.finish" color="secondary" class="mr-2">
                    <CIcon name="cilImage" size="sm" class="mr-1"/>
                    {{ job.print_config.finish }}
                  </CBadge>
                </div>

                <span v-if="getSalNo(job)" class="text-muted small mr-3">
                  Order #{{ getSalNo(job) }}
                </span>
              </div>
            </div>

            <div class="print-job-actions">
              <CBadge
                v-if="job.media_ids && job.media_ids.length > 0 && isExpanded(job.id) && job.pica_service_id"
                color="light"
                class="mr-3 photos-badge clickable"
                @click="$emit('toggle-select-all', job.id)"
                :title="$pgettext('edge-prints.jobs', 'Click to select/deselect all')"
              >
                <CIcon name="cipPicture" size="sm" class="mr-1"/>
                {{ job.media_ids.length }}
                <span v-if="job.quantity > 1" class="ml-1">
                  × {{ job.quantity }}
                </span>
              </CBadge>

              <CBadge
                v-else-if="job.media_ids && job.media_ids.length > 0"
                color="light"
                class="mr-3 photos-badge"
              >
                <CIcon name="cipPicture" size="sm" class="mr-1"/>
                {{ job.media_ids.length }}
                <span v-if="job.quantity > 1" class="ml-1">
                  × {{ job.quantity }}
                </span>
              </CBadge>

              <span class="text-muted small mr-3">
                {{ job.created_at | formatDateTime }}
              </span>

              <CButton
                v-if="expandable && job.media_ids && job.media_ids.length > 0 && job.pica_service_id"
                color="primary"
                variant="ghost"
                size="sm"
                @click="toggleExpand(job.id)"
                class="expand-btn"
              >
                <CIcon :name="isExpanded(job.id) ? 'cilChevronTop' : 'cilChevronBottom'"/>
                <span class="ml-1">{{ isExpanded(job.id) ? $pgettext('edge-prints.jobs', 'Hide') : $pgettext('edge-prints.jobs', 'View') }}</span>
              </CButton>
            </div>
          </div>

          <!-- Expandable Photos -->
          <div v-if="expandable && isExpanded(job.id)" class="print-job-photos">
            <slot name="photos" :job="job">
              <!-- Slot for custom photo display -->
            </slot>
          </div>
        </div>
      </div>
    </div>

    <CCard v-else-if="!loading">
      <CCardBody class="text-center text-muted py-5">
        <CIcon name="cilPrint" size="3xl" class="mb-3 opacity-50"/>
        <p>
          <translate translate-context="edge-prints.jobs">No print jobs found</translate>
        </p>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'PrintJobList',
  props: {
    printJobs: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    expandable: {
      type: Boolean,
      default: true
    },
    mediaComponent: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      expandedJobs: []
    }
  },
  methods: {
    toggleExpand(jobId) {
      const index = this.expandedJobs.indexOf(jobId)
      if (index > -1) {
        this.expandedJobs.splice(index, 1)
      } else {
        this.expandedJobs.push(jobId)
      }
    },

    isExpanded(jobId) {
      return this.expandedJobs.includes(jobId)
    },

    getSalNo(job) {
      return (job.payment_data &&
        job.payment_data.metadata &&
        job.payment_data.metadata.sapi &&
        job.payment_data.metadata.sapi.sale_response &&
        job.payment_data.metadata.sapi.sale_response.SalNo) || null
    }
  },
  filters: {
    formatDateTime: Filters.formatDateTime
  }
}
</script>

<style scoped>
.list-header {
  font-weight: 600;
}

.print-jobs-container {
  /* Container for all print job items */
}

.print-job-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  background: white;
  transition: all 0.2s ease;
}

.print-job-item:last-child {
  margin-bottom: 0;
}

.print-job-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border-color: #d0d5dd;
}

.print-job-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.print-job-info {
  flex: 1;
  display: flex;
  align-items: center;
}

.job-specs {
  display: flex;
  align-items: center;
}

.photos-badge {
  font-weight: 500;
  padding: 0.35rem 0.65rem;
  border: 1px solid #dee2e6;
}

.photos-badge.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.photos-badge.clickable:hover {
  background-color: #e7f3ff;
  border-color: #0d6efd;
  transform: translateY(-1px);
}

.print-job-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.expand-btn {
  font-weight: 500;
  padding: 0.375rem 0.75rem;
}

.expand-btn:hover {
  background-color: #e7f3ff;
}

.print-job-photos {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

@media (max-width: 768px) {
  .print-job-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .print-job-actions {
    margin-top: 0.75rem;
    width: 100%;
    justify-content: space-between;
  }

  .job-specs {
    flex-wrap: wrap;
    margin-top: 0.5rem;
  }
}
</style>
